'use client'

import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'

const POPULAR_EMOJIS = ['👨', '👩', '👧', '👦', '🧑', '🧔', '👵', '👴', '💰', '🐻', '🐂', '🦊', '🎯', '📊', '🚀', '💎', '👑', '🎪', '🌟', '⭐', '🔥', '💼', '🏦', '🇨🇭', '🇩🇪', '🐕', '🧀', '📈', '🏠', '🚲', '⛷️', '🍕']
export { POPULAR_EMOJIS }

/** Format a full name according to the team's nameFormat setting */
export function formatName(name: string, format?: 'full' | 'last_first' | 'abbrev'): string {
  if (!name) return ''
  const parts = name.trim().split(/\s+/)
  if (parts.length < 2 || !format || format === 'full') return name
  const first = parts[0]
  const last = parts.slice(1).join(' ')
  if (format === 'last_first') return `${last}, ${first}`
  if (format === 'abbrev') return `${first.charAt(0)}. ${last}`
  return name
}

interface EmojiRegistryContextValue {
  getEmoji: (name: string) => string
  getEmojiByEmail: (email: string) => string
  setEmoji: (name: string, emoji: string) => void
  getSettings: (emailOrName?: string) => { numberFormat: 'CH' | 'DE'; dateFormat: 'DD.MM.YYYY' | 'YYYY-MM-DD' }
  globalSettings: { sectors: SectorDef[], categoryStyles: Record<string, CategoryStyle>, weatherLocations?: WeatherLocation[], teamName?: string, teamEmoji?: string, nameFormat?: 'full' | 'last_first' | 'abbrev', ownerEmojis?: Record<string, string>, ownerFormats?: Record<string, 'CH' | 'DE'>, ownerRoles?: Record<string, string>, ownerLangs?: Record<string, string>, ownerTaxResidency?: Record<string, string>, displayCurrency?: 'EUR' | 'CHF' | 'USD' | 'GBP', ownerCurrencies?: Record<string, 'EUR' | 'CHF' | 'USD' | 'GBP'> }
  refreshGlobalSettings: () => Promise<void>
  updateGlobalSettings: (settings: any) => Promise<{ error?: any }>
  getPortfolioStyle: (p: { name?: string, type?: string }) => { color: string, emoji: string, type: string }
}

export interface WeatherLocation {
  name: string
  lat: string
  lon: string
}

interface SectorDef {
  name: string
  color: string
  emoji: string
}

interface CategoryStyle {
  color: string
  emoji: string
}

const EmojiRegistryContext = createContext<EmojiRegistryContextValue>({
  getEmoji: () => '👤',
  getEmojiByEmail: () => '👤',
  setEmoji: () => {},
  getSettings: () => ({ numberFormat: 'CH', dateFormat: 'DD.MM.YYYY' }),
  globalSettings: { sectors: [], categoryStyles: {}, weatherLocations: [], teamName: '', teamEmoji: '👨‍👩‍👧‍👦', ownerEmojis: {}, ownerFormats: {}, ownerRoles: {} },
  refreshGlobalSettings: async () => {},
  updateGlobalSettings: async () => ({ error: 'Not implemented' }),
  getPortfolioStyle: () => ({ color: '#3b82f6', emoji: '📈', type: 'aktien' })
})

const STORAGE_KEY = 'portfolio-emojis-v2'

function loadLocalEmojis(): Record<string, string> {
  try {
    if (typeof window === 'undefined') return {}
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    // Sanitize: only keep string values so nothing non-renderable reaches JSX
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(parsed)) {
      if (typeof v === 'string') out[k] = v
    }
    return out
  } catch {
    return {}
  }
}

function getDefaultEmoji(name: string): string {
  // Family-name → emoji defaults removed: they were leaking through on first
  // paint when ownerEmojis hadn't hydrated yet, showing wrong personalized
  // icons to fresh logins. Real emojis come from /api/settings → ownerEmojis.
  // Nick AI is the one exception that's a system identity, not a person.
  if ((name || '').trim().toLowerCase().includes('nick')) return '🤖'
  return '👤'
}

const SETTINGS_CACHE_KEY = 'fuchs-global-settings-v1'

function loadCachedGlobalSettings(): { ownerEmojis: Record<string, string>, ownerFormats: Record<string, 'CH' | 'DE'>, teamName: string, teamEmoji: string } | null {
  try {
    if (typeof window === 'undefined') return null
    const raw = localStorage.getItem(SETTINGS_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch {
    return null
  }
}

export function EmojiRegistryProvider({ children }: { children: React.ReactNode }) {
  const [emojis, setEmojis] = useState<Record<string, string>>({})
  const [emailEmojis, setEmailEmojis] = useState<Record<string, string>>({})
  const [userSettings, setUserSettings] = useState<Record<string, { numberFormat: string; dateFormat: string }>>({})
  const [globalSettings, setGlobalSettings] = useState<{ sectors: SectorDef[], categoryStyles: Record<string, CategoryStyle>, weatherLocations: WeatherLocation[], teamName?: string, teamEmoji?: string, nameFormat?: 'full' | 'last_first' | 'abbrev', ownerEmojis?: Record<string, string>, ownerFormats?: Record<string, 'CH' | 'DE'>, ownerRoles?: Record<string, string>, ownerLangs?: Record<string, string>, ownerTaxResidency?: Record<string, string>, displayCurrency?: 'EUR' | 'CHF' | 'USD' | 'GBP', ownerCurrencies?: Record<string, 'EUR' | 'CHF' | 'USD' | 'GBP'> }>({ sectors: [], categoryStyles: {}, weatherLocations: [], teamName: '', teamEmoji: '👨‍👩‍👧‍👦', ownerEmojis: {}, ownerFormats: {}, ownerRoles: {}, ownerTaxResidency: {} })

  const syncWithSupabase = useCallback(async () => {
    try {
      // 1. Fetch Global Settings directly from its dedicated API
      const settingsRes = await fetch('/api/settings', { cache: 'no-store' })
      if (settingsRes.ok) {
        const data = await settingsRes.json()
        // Sanitize: JSONB fields can be objects — coerce to strings to prevent React #300
        const asStr = (v: unknown, fallback: string): string => (typeof v === 'string' ? v : fallback)
        const asStrMap = (v: unknown): Record<string, string> => {
          if (!v || typeof v !== 'object') return {}
          const out: Record<string, string> = {}
          for (const [k, val] of Object.entries(v)) {
            if (typeof val === 'string') out[k] = val
          }
          return out
        }
        const next = {
          sectors: Array.isArray(data.sectors) ? data.sectors : [],
          categoryStyles: (data.categoryStyles && typeof data.categoryStyles === 'object') ? data.categoryStyles : {},
          teamName: asStr(data.teamName, 'Familie'),
          teamEmoji: asStr(data.teamEmoji, '👨‍👩‍👧‍👦'),
          ownerEmojis: asStrMap(data.ownerEmojis),
          ownerFormats: (data.ownerFormats && typeof data.ownerFormats === 'object') ? data.ownerFormats : {},
          ownerRoles: asStrMap(data.ownerRoles),
          weatherLocations: Array.isArray(data.weatherLocations) ? data.weatherLocations : [],
        }
        setGlobalSettings(next)
        // Persist a slim slice for the next mount so the first paint after
        // refresh already has the right emojis instead of flashing defaults.
        try {
          if (typeof window !== 'undefined') {
            localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify({
              ownerEmojis: next.ownerEmojis,
              ownerFormats: next.ownerFormats,
              teamName: next.teamName,
              teamEmoji: next.teamEmoji,
            }))
          }
        } catch { /* ignore quota errors */ }
      }

      // 2. Fetch users for individual settings and fallback emojis
      const res = await fetch('/api/users', { cache: 'no-store' })
      if (res.status === 401) return

      if (res.ok) {
        const { users } = await res.json()
        if (!users) return

        const dbEmojis: Record<string, string> = {}
        // No hardcoded family-email defaults — let everything come from the
        // database so a fresh login never sees stale personalized icons.
        const dbEmailEmojis: Record<string, string> = {}
        const dbSettings: Record<string, any> = {}

        for (const u of users) {
          if (u.name === 'GlobalSettings') continue
          if (u.metadata?.emoji && typeof u.metadata.emoji === 'string') {
            const emoji = u.metadata.emoji
            dbEmojis[u.name.toLowerCase()] = emoji
            if (u.email) dbEmailEmojis[u.email.toLowerCase()] = emoji
            if (u.name.includes('@')) dbEmailEmojis[u.name.toLowerCase()] = emoji
          }
          dbSettings[u.email?.toLowerCase() || u.name.toLowerCase()] = {
            numberFormat: u.metadata?.numberFormat || 'CH',
            dateFormat: u.metadata?.dateFormat || 'DD.MM.YYYY'
          }
        }
        setEmojis(prev => ({ ...prev, ...dbEmojis }))
        setEmailEmojis(dbEmailEmojis)
        setUserSettings(dbSettings)
      }
    } catch (err) {
      console.warn('Emoji sync failed:', err)
    }
  }, [])

  useEffect(() => {
    setEmojis(loadLocalEmojis())
    // Hydrate globalSettings from localStorage cache before the API call
    // returns. Without this, every fresh page load briefly renders generic
    // 👤 placeholders before swapping to the real owner emojis.
    const cached = loadCachedGlobalSettings()
    if (cached) {
      setGlobalSettings(prev => ({
        ...prev,
        ownerEmojis: cached.ownerEmojis || {},
        ownerFormats: cached.ownerFormats || {},
        teamName: cached.teamName || prev.teamName,
        teamEmoji: cached.teamEmoji || prev.teamEmoji,
      }))
    }
    syncWithSupabase()
    const interval = setInterval(() => { if (typeof document !== 'undefined' && document.visibilityState === 'visible') syncWithSupabase() }, 300000)
    return () => clearInterval(interval)
  }, [syncWithSupabase])

  // Final guard — ensures nothing non-string ever reaches JSX from this registry
  const asStr = (v: unknown, fallback = '👤'): string => (typeof v === 'string' && v.length > 0 ? v : fallback)

  const getEmoji = (name: string) => {
    if (!name || typeof name !== 'string') return '👤'
    const clean = name.trim().toLowerCase()

    // 1. Check ownerEmojis in globalSettings (Database prioritized)
    const dbOwnerEmojis = globalSettings.ownerEmojis || {}
    const dbMatch = Object.entries(dbOwnerEmojis).find(([k]) => k.toLowerCase() === clean)
    if (dbMatch) return asStr(dbMatch[1])

    // 2. Check local/legacy user metadata
    if (emojis[clean]) return asStr(emojis[clean])

    // 3. Fallback to hardcoded defaults
    return asStr(getDefaultEmoji(name))
  }

  const getEmojiByEmail = (email: string) => {
    if (!email || typeof email !== 'string') return '👤'
    const clean = email.trim().toLowerCase()
    if (emailEmojis[clean]) return asStr(emailEmojis[clean])
    return asStr(emojis[clean])
  }

  const setEmoji = (name: string, emoji: string) => {
    if (!name) return
    const clean = name.trim().toLowerCase()
    const next = { ...emojis, [clean]: emoji }
    setEmojis(next)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {}
    tryPatchSupabaseEmoji(name.trim(), emoji).catch(() => {})
  }

  const getSettings = (emailOrName?: string) => {
    if (!emailOrName) return { numberFormat: 'CH' as const, dateFormat: 'DD.MM.YYYY' as const }
    const clean = emailOrName.toLowerCase()
    
    // 1. Check database-backed owner formats from GlobalSettings
    const dbFormats = globalSettings.ownerFormats || {}
    const dbMatch = Object.entries(dbFormats).find(([k]) => k.toLowerCase() === clean)
    if (dbMatch) {
      return { numberFormat: dbMatch[1], dateFormat: 'DD.MM.YYYY' as const }
    }

    // 2. Fallback to individual legacy metadata
    const s = userSettings[clean]
    return {
      numberFormat: (s?.numberFormat || 'CH') as 'CH' | 'DE',
      dateFormat: (s?.dateFormat || 'DD.MM.YYYY') as 'DD.MM.YYYY' | 'YYYY-MM-DD'
    }
  }

  const getPortfolioStyle = useCallback((p: { name?: string, type?: string }) => {
    if (!p) return { color: '#3b82f6', emoji: '📈', type: 'aktien' }
    
    const name = p.name || ''
    const rawType = (p.type || '').toLowerCase()
    
    // 1. Determine Normalized Type
    let type = 'aktien'
    const nameMap: Record<string, string> = {
      'PK Nest+Frankly+Viac': 'vorsorge',
      'Bitcoin': 'crypto',
      'Haushalt': 'sonstige',
      'Göblistrasse Colin D203': 'immobilien',
      'Bayreuth Whg 12+15': 'immobilien',
      'Haus Bayreuth': 'immobilien'
    }
    
    if (nameMap[name]) {
      type = nameMap[name]
    } else if (rawType === 'pension' || rawType === 'vorsorge') {
      type = 'vorsorge'
    } else if (rawType === 'crypto') {
      type = 'crypto'
    } else if (rawType === 'immobilien') {
      type = 'immobilien'
    } else if (rawType === 'cash') {
      type = 'cash'
    } else if (rawType === 'schulden' || rawType === 'debt') {
      type = 'schulden'
    } else if (rawType === 'sonstige' || rawType === 'other') {
      type = 'sonstige'
    }

    // 2. Get Style from Global Settings
    const style = globalSettings?.categoryStyles?.[type]
    const defaultIcons: Record<string, string> = { aktien: '📈', crypto: '₿', immobilien: '🏠', vorsorge: '🏛️', cash: '💶', schulden: '🏦', sonstige: '📁' }
    const defaultColors: Record<string, string> = { aktien: '#3b82f6', crypto: '#f97316', immobilien: '#f59e0b', vorsorge: '#8b5cf6', cash: '#10b981', schulden: '#ef4444', sonstige: '#6b7280' }

    return {
      type,
      color: style?.color || defaultColors[type] || '#3b82f6',
      emoji: style?.emoji || defaultIcons[type] || '📈'
    }
  }, [globalSettings])

  const updateGlobalSettings = async (newSettings: any) => {
    try {
      const res = await fetch('/api/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSettings)
      })
      if (!res.ok) {
        const err = await res.json()
        return { error: err }
      }
      setGlobalSettings(newSettings)
      return {}
    } catch (err) {
      return { error: err }
    }
  }

  // Memoized provider value — a fresh object literal every render re-rendered
  // every consumer app-wide (the registry wraps the entire app).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ctxValue = useMemo(
    () => ({ getEmoji, getEmojiByEmail, setEmoji, getSettings, globalSettings, refreshGlobalSettings: syncWithSupabase, getPortfolioStyle, updateGlobalSettings }),
    [emojis, emailEmojis, userSettings, globalSettings, getPortfolioStyle],
  )

  return (
    <EmojiRegistryContext.Provider value={ctxValue}>
      {children}
    </EmojiRegistryContext.Provider>
  )
}

export function useEmojiRegistry() {
  return useContext(EmojiRegistryContext)
}

export const useEmojiStore = useEmojiRegistry

async function tryPatchSupabaseEmoji(name: string, emoji: string) {
  try {
    await fetch('/api/owner/avatar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, emoji }),
    })
  } catch { /* fail gracefully */ }
}
